import React, { useCallback, useEffect, useState } from 'react';
import { SlidersHorizontal, UserPlus, Users, X } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../services/api';
import PatientAssignmentsPanel from '../components/PatientAssignmentsPanel';
import PermissionOverridesModal from '../components/PermissionOverridesModal';

const ROLES = [
  ['dentist', 'Dentista'], ['secretary', 'Secretária'], ['assistant', 'Auxiliar / ASB / TSB'],
  ['financial', 'Financeiro'], ['reception', 'Recepção'],
];
const roleName = (id) => ROLES.find(([value]) => value === id)?.[1] || (id === 'clinic_admin' ? 'Administrador da clínica' : id);

export default function ClinicUsersPage() {
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [permissionMember, setPermissionMember] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', role_id: 'secretary', professional_id: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [membersResponse, invitationsResponse, professionalsResponse] = await Promise.all([
        api.get('/clinic-users'), api.get('/clinic-users/invitations'), api.get('/clinic-users/professionals'),
      ]);
      setMembers(membersResponse.data || []);
      setInvitations(invitationsResponse.data || []);
      setProfessionals(professionalsResponse.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Não foi possível carregar os usuários.' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const invite = async (event) => {
    event.preventDefault();
    try {
      setSaving(true); setMessage(null);
      await api.post('/clinic-users/invitations', {
        ...form,
        professional_id: form.role_id === 'dentist' ? (form.professional_id || null) : null,
      });
      setModalOpen(false);
      setForm({ full_name: '', email: '', role_id: 'secretary', professional_id: '' });
      setMessage({ type: 'success', text: 'Convite enviado com sucesso.' });
      await load();
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Não foi possível enviar o convite.' });
    } finally { setSaving(false); }
  };

  const updateMember = async (membershipId, changes) => {
    try {
      setMessage(null);
      await api.patch(`/clinic-users/${membershipId}`, changes);
      setMessage({ type: 'success', text: 'Acesso atualizado com sucesso.' });
      await load();
      return true;
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Não foi possível atualizar o acesso.' });
      return false;
    }
  };

  const changeRole = async (member, roleId) => {
    const overrides = member.permission_overrides || {};
    const customized = (overrides.allow?.length || 0) + (overrides.deny?.length || 0);
    if (customized && !window.confirm('Alterar o perfil restaurará as permissões personalizadas para o padrão do novo perfil. Continuar?')) return;
    await updateMember(member.id, { role_id: roleId });
  };

  const cancelInvitation = async (id) => {
    if (!window.confirm('Cancelar este convite pendente?')) return;
    try {
      await api.delete(`/clinic-users/invitations/${id}`);
      setMessage({ type: 'success', text: 'Convite cancelado.' });
      await load();
    } catch (error) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Não foi possível cancelar o convite.' });
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-8 px-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Users className="w-6 h-6 text-purple-600" /> Usuários e permissões</h1>
            <p className="text-sm text-gray-500 mt-1">Cada colaborador utiliza seu próprio e-mail e senha.</p>
          </div>
          <button onClick={() => setModalOpen(true)} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700">
            <UserPlus className="w-4 h-4" /> Convidar colaborador
          </button>
        </div>

        {message && <div className={`mb-4 rounded-lg border p-3 text-sm ${message.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>{message.text}</div>}

        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b"><h2 className="font-semibold text-gray-900">Pessoas com acesso</h2></div>
          {loading ? <div className="p-8 text-center text-gray-500">Carregando...</div> : members.length === 0 ? <div className="p-8 text-center text-gray-500">Nenhum usuário encontrado.</div> : (
            <div className="divide-y">
              {[...members].sort((a, b) => {
                const aAdmin = a.role_id === 'clinic_admin' ? 0 : 1;
                const bAdmin = b.role_id === 'clinic_admin' ? 0 : 1;
                return aAdmin - bAdmin;
              }).map((member) => {
                const isOwner = member.role_id === 'clinic_admin';
                const active = member.status === 'active';
                const isDentist = !isOwner && member.role_id === 'dentist';
                return <div key={member.id} className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1 min-w-0 text-left">
                      <p className="font-medium text-gray-900 truncate">{member.user?.full_name || member.user?.email || 'Usuário'}</p>
                      <p className="text-sm text-gray-500 truncate">{member.user?.email}</p>
                      <span className={`inline-flex mt-2 px-2 py-0.5 rounded-full text-xs ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{active ? 'Ativo' : member.status === 'suspended' ? 'Suspenso' : 'Revogado'}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
                      <select disabled={isOwner} value={member.role_id} onChange={(e) => changeRole(member, e.target.value)} className="border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100">
                        {isOwner && <option value="clinic_admin">Administrador da clínica</option>}
                        {ROLES.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                      </select>
                      {!isOwner && <button type="button" onClick={() => setPermissionMember(member)} className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm border text-purple-700 hover:bg-purple-50 whitespace-nowrap"><SlidersHorizontal className="w-4 h-4" /> Permissões{((member.permission_overrides?.allow?.length || 0) + (member.permission_overrides?.deny?.length || 0)) > 0 ? ` (${(member.permission_overrides?.allow?.length || 0) + (member.permission_overrides?.deny?.length || 0)})` : ''}</button>}
                      {!isOwner && <button onClick={() => updateMember(member.id, { status: active ? 'suspended' : 'active' })} className={`px-3 py-2 rounded-lg text-sm border whitespace-nowrap ${active ? 'text-amber-700 border-amber-300 hover:bg-amber-50' : 'text-emerald-700 border-emerald-300 hover:bg-emerald-50'}`}>{active ? 'Suspender' : 'Reativar'}</button>}
                    </div>
                  </div>
                  {isDentist && <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:justify-end">
                    <select value={member.professional_id || ''} onChange={(e) => updateMember(member.id, { professional_id: e.target.value || null })} className="border rounded-lg px-3 py-2 text-sm sm:min-w-56">
                      <option value="">Sem vínculo profissional</option>
                      {professionals.map((professional) => <option key={professional.id} value={professional.id}>{professional.name}{professional.cro_number ? ` · CRO ${professional.cro_state || ''} ${professional.cro_number}` : ''}</option>)}
                    </select>
                    <select value={member.patient_scope || 'all'} onChange={(e) => updateMember(member.id, { patient_scope: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" title="Define quais prontuários o dentista pode acessar">
                      <option value="all">Pacientes: todos da clínica</option>
                      <option value="assigned">Pacientes: somente atribuídos</option>
                    </select>
                    <select value={member.agenda_scope || 'all'} onChange={(e) => updateMember(member.id, { agenda_scope: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" title="Define a visualização da agenda do dentista">
                      <option value="all">Agenda: completa da clínica</option>
                      <option value="busy_only">Agenda: geral com privacidade</option>
                      <option value="own">Agenda: somente a própria</option>
                    </select>
                  </div>}
                </div>;
              })}
            </div>
          )}
        </div>

        {invitations.length > 0 && <div className="bg-white border rounded-xl shadow-sm overflow-hidden mt-6">
          <div className="px-5 py-4 border-b"><h2 className="font-semibold text-gray-900">Convites pendentes</h2></div>
          <div className="divide-y">{invitations.map((inviteItem) => <div key={inviteItem.id} className="p-5 flex items-center gap-3">
            <div className="flex-1 min-w-0"><p className="font-medium text-gray-900 truncate">{inviteItem.full_name}</p><p className="text-sm text-gray-500 truncate">{inviteItem.email} · {roleName(inviteItem.role_id)}</p></div>
            <button onClick={() => cancelInvitation(inviteItem.id)} className="text-sm text-red-600 hover:text-red-700">Cancelar</button>
          </div>)}</div>
        </div>}

        <PatientAssignmentsPanel professionals={professionals} onMessage={setMessage} />
        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">Em “somente atribuídos”, o dentista acessa pacientes em que esteja definido como profissional principal ou compartilhado. O escopo da Agenda é independente dessa escolha.</div>
      </div>

      {modalOpen && <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}>
        <form onSubmit={invite} className="w-full max-w-md bg-white rounded-xl shadow-xl p-6">
          <div className="flex items-center justify-between mb-5"><h2 className="text-lg font-semibold">Convidar colaborador</h2><button type="button" onClick={() => setModalOpen(false)}><X className="w-5 h-5" /></button></div>
          <label className="block text-sm font-medium mb-1">Nome</label><input required minLength={2} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full border rounded-lg px-3 py-2 mb-4" />
          <label className="block text-sm font-medium mb-1">E-mail</label><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border rounded-lg px-3 py-2 mb-4" />
          <label className="block text-sm font-medium mb-1">Perfil de acesso</label><select value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value, professional_id: e.target.value === 'dentist' ? form.professional_id : '' })} className="w-full border rounded-lg px-3 py-2 mb-4">{ROLES.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select>
          {form.role_id === 'dentist' && <><label className="block text-sm font-medium mb-1">Vincular ao profissional</label><select value={form.professional_id} onChange={(e) => setForm({ ...form, professional_id: e.target.value })} className="w-full border rounded-lg px-3 py-2 mb-4"><option value="">Vincular depois</option>{professionals.map((professional) => <option key={professional.id} value={professional.id}>{professional.name}{professional.cro_number ? ` · CRO ${professional.cro_state || ''} ${professional.cro_number}` : ''}</option>)}</select></>}
          <p className="text-xs text-gray-500 mb-5">O convite será válido por 72 horas. O colaborador criará a própria senha.</p>
          <div className="flex justify-end gap-2"><button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border rounded-lg">Cancelar</button><button disabled={saving} className="px-4 py-2 bg-purple-600 text-white rounded-lg disabled:opacity-60">{saving ? 'Enviando...' : 'Enviar convite'}</button></div>
        </form>
      </div>}
      {permissionMember && <PermissionOverridesModal member={permissionMember} onClose={() => setPermissionMember(null)} onSave={(permissionOverrides) => updateMember(permissionMember.id, { permission_overrides: permissionOverrides })} />}
    </Layout>
  );
}
